import { Children, isValidElement, useState } from 'react';
import { Pressable, useColorScheme } from 'react-native';
import { View, XStack, YStack } from 'tamagui';

import { Text } from '@/components/text';
import { ChevronRight as ChevronRightIcon } from 'lucide-react-native';
import { cardStyle } from '@/components/card';
import { darkColors, lightColors, space } from '@/theme/tokens.generated';
import { hit } from '@/lib/hit-slop';

// Issue #289 -- the founder's whole-app critique's actual root cause,
// not any single screen: Settings and Profile's Prompts/Photos sections
// were six-plus centered text links with no count, preview, or status,
// each well under the 44px WCAG 2.5.8 target-size floor. This is the
// real settings-row molecule the issue asks for: full-width, left label
// + right value/chevron, $card background, grouped into one rounded
// section per SettingsSection below.
//
// Raw Pressable + explicit hover/focus/press state, NOT Card -- Card's
// own per-item rounded corners + shadow read as a single floating tile,
// exactly wrong for a GROUPED list where only the section's outer edges
// should round and adjacent rows should read as one continuous surface
// (a real iOS/Android Settings list, and this design system's own
// "One thing leads" -- composition.md -- convention: a section is one
// visual unit, not a stack of separately-elevated cards). Card's own
// doc comment already proved why a Tamagui styled(YStack) loses real
// DOM role/keyboard focus on web -- same reasoning applies here, so
// this follows TextLink's identical proven pattern (raw Pressable, a
// real outline on focus using the same actionPrimary hex pair, not a
// Tamagui outlineColor theme reference) rather than re-deriving it.
export interface SettingsRowProps {
  label: string;
  value?: string;
  onPress: () => void;
  destructive?: boolean;
  loading?: boolean;
}

export function SettingsRow({ label, value, onPress, destructive, loading }: SettingsRowProps) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const colorScheme = useColorScheme();
  const colors = colorScheme === 'dark' ? darkColors : lightColors;
  const outlineColor = colors.actionPrimary;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={value ? `${label}, ${value}` : label}
      {...hit(0)}
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={({ pressed }) => [
        {
          // 44px WCAG 2.5.8 floor -- issue #289's own explicit ask,
          // real height (not padding alone) so a short single-line
          // label/value pair never falls under it.
          minHeight: 44,
          paddingHorizontal: space[4],
          paddingVertical: space[3],
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: space[3],
        },
        hovered ? { backgroundColor: colors.surfaceSunken } : null,
        pressed ? { opacity: 0.85 } : null,
        focused ? { outlineStyle: 'solid', outlineWidth: 2, outlineColor, outlineOffset: -2 } : null,
      ]}
    >
      <Text
        fontFamily="$body"
        color={destructive ? '$destructive' : '$color'}
        fontSize="$body"
        lineHeight="$body"
        fontWeight="$medium"
        flexShrink={1}
        numberOfLines={2}
      >
        {label}
      </Text>
      <XStack alignItems="center" gap="$2" flexShrink={1} maxWidth="65%">
        {value && (
          <Text
            fontFamily="$body"
            color="$colorSecondary"
            fontSize="$meta"
            lineHeight="$meta"
            numberOfLines={2}
            flexShrink={1}
            textAlign="right"
          >
            {loading ? '...' : value}
          </Text>
        )}
        <ChevronRightIcon size={16} strokeWidth={1.75} color={colors.textTertiary} />
      </XStack>
    </Pressable>
  );
}

// Groups SettingsRow children into one rounded, bordered surface with
// hairline dividers between rows (not around the group's own outer
// edge -- that's cardStyle's own border) -- Children.map + a divider
// inserted before every row but the first, rather than a `gap` on the
// outer YStack, since a gap would leave a visible seam of bare page
// background between rows instead of one continuous $card surface.
export function SettingsSection({ children }: { children: React.ReactNode }) {
  const rows = Children.toArray(children).filter(isValidElement);

  return (
    <YStack {...cardStyle} overflow="hidden">
      {rows.map((row, index) => (
        <View key={row.key ?? index}>
          {index > 0 && <View height={1} backgroundColor="$borderColor" />}
          {row}
        </View>
      ))}
    </YStack>
  );
}
